// File: components/GlobalTopBar.tsx

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  SearchIcon,
  CalendarCheck2Icon,
  UserPlus2Icon,
  ChevronDown,
  LogOut,
  User,
  Settings,
  Activity,
  Clock,
  Menu,
  X,
} from "lucide-react";
import NewPatientModal from "@/components/Patient/NewPatientModal";
import SearchOverlay from "@/components/SearchOverlay";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

export default function GlobalTopBar() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { theme } = useTheme();
  const [search, setSearch] = useState("");
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Enhanced keyboard shortcuts
      if (e.key === "Escape") {
        setShowSearch(false);
        setShowDropdown(false);
        setShowMobileMenu(false);
      }
      // Cmd/Ctrl + K for search (modern pattern)
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      }
      // Cmd/Ctrl + N for new patient (quick action)
      if ((e.metaKey || e.ctrlKey) && e.key === "n") {
        e.preventDefault();
        setShowAddPatient(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setLoading(true);
    setTimeout(() => {
      logout?.();
      setShowDropdown(false);
      setShowMobileMenu(false);
      navigate("/login");
    }, 500); // Small delay for better UX
  };

  const isDoctor = currentUser?.role?.toLowerCase() === "doctor";

  const formatUserName = (name: string | undefined) => {
    if (!name) return isDoctor ? "Doctor" : "User";
    // Smart name formatting - remove duplicate "Dr." if present and handle edge cases
    let formattedName = name;
    
    // Remove leading "Dr." or "Doctor" since we add it manually (for doctors)
    if (formattedName.toLowerCase().startsWith('dr. ')) {
      formattedName = formattedName.substring(4);
    } else if (formattedName.toLowerCase().startsWith('doctor ')) {
      formattedName = formattedName.substring(7);
    }
    
    return formattedName;
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return "D";
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 border-b border-border shadow-sm transition-all duration-200">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">
          
          {/* Left: Enhanced Brand/Logo - Always visible */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2">
              <img src="/onecheckup-icon.png" alt="OneCheckUp" className="w-8 h-8 object-contain" />
              <div className="flex flex-col">
                <span className="text-sm font-bold text-foreground">One Check Up</span>
                <span className="text-[10px] text-muted-foreground -mt-0.5">EHR System</span>
              </div>
            </div>
          </div>

          {/* Center: Enhanced Search */}
          <div className="flex-1 max-w-2xl mx-4 sm:mx-6 ml-4 sm:ml-6">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="global-search"
                name="global-search"
                placeholder="Search patients, appointments... (⌘K)"
                className="w-full pl-10 pr-16 text-sm bg-muted/50 focus:bg-background focus:border-primary transition-all duration-200 hover:bg-muted/70 h-10"
                value={search}
                onFocus={() => setShowSearch(true)}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                <kbd className="hidden lg:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted border-border px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </div>
            </div>
          </div>

          {/* Right: Enhanced Action Controls */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile Search Button */}
            <Button
              variant="ghost"
              size="sm"
              className="sm:hidden p-2 hover:bg-accent h-9 w-9"
              onClick={() => setShowSearch(true)}
            >
              <SearchIcon className="w-4 h-4" />
            </Button>

            {/* Primary Action: New Patient */}
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowAddPatient(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-200 group shadow-sm font-medium px-3 sm:px-4 h-9"
            >
              <UserPlus2Icon className="w-4 h-4 mr-1 sm:mr-2 group-hover:scale-110 transition-transform" />
              <span className="hidden sm:inline text-sm">New Patient</span>
              <span className="sm:hidden text-xs">New</span>
            </Button>

            {/* Secondary Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Enhanced Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 hover:bg-accent p-2 transition-all duration-200 group h-9"
                  onClick={() => setShowDropdown(!showDropdown)}
                  disabled={loading}
                >
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      {loading ? (
                        <div className="w-7 h-7 rounded-full bg-muted animate-pulse"></div>
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary via-primary/90 to-primary/70 flex items-center justify-center text-primary-foreground text-xs font-semibold shadow-sm group-hover:shadow-md transition-shadow">
                          {getInitials(currentUser?.name)}
                        </div>
                      )}
                      <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-success rounded-full border-2 border-background"></div>
                    </div>
                    <div className="hidden md:block text-left">
                      <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                        {isDoctor ? `Dr. ${formatUserName(currentUser?.name)}` : formatUserName(currentUser?.name)}
                      </div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />
                        {currentUser?.role || "Doctor"} • Online
                      </div>
                    </div>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-all duration-200 hidden md:block" />
                </Button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                    <div className="p-4 border-b border-border bg-gradient-to-r from-primary/5 to-primary/10">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground font-semibold">
                          {getInitials(currentUser?.name)}
                        </div>
                        <div>
                          <div className="font-medium text-sm text-popover-foreground">
                            {isDoctor ? `Dr. ${formatUserName(currentUser?.name)}` : formatUserName(currentUser?.name)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {currentUser?.email || ""}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="py-1">
                      {isDoctor && (
                        <>
                          <button 
                            className="flex items-center w-full text-left px-4 py-3 text-sm hover:bg-muted/50 transition-colors group"
                            onClick={() => {
                              navigate("/settings");
                              setShowDropdown(false);
                            }}
                          >
                            <Settings className="w-4 h-4 mr-3 text-muted-foreground group-hover:text-primary transition-colors" />
                            <div>
                              <div className="font-medium">Settings</div>
                              <div className="text-xs text-muted-foreground">Preferences and configuration</div>
                            </div>
                          </button>
                          <div className="my-1 border-t border-border"></div>
                        </>
                      )}
                      <button 
                        className="flex items-center w-full text-left px-4 py-3 text-sm text-destructive hover:bg-destructive/10 transition-colors group"
                        onClick={handleLogout}
                        disabled={loading}
                      >
                        <LogOut className="w-4 h-4 mr-3 group-hover:text-destructive transition-colors" />
                        <div>
                          <div className="font-medium">
                            {loading ? "Signing out..." : "Log out"}
                          </div>
                          <div className="text-xs text-muted-foreground">Sign out of your account</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <NewPatientModal isOpen={showAddPatient} onClose={() => setShowAddPatient(false)} />
      <SearchOverlay open={showSearch} onClose={() => setShowSearch(false)} />
    </>
  );
}